import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

interface HowToPlayModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameTitle: string;
  gameIcon: string;
  objective?: string;
  rules: (string | React.ReactNode)[];
  author?: string;
  howToPlay?: string;
}

/**
 * Modal "Cómo se juega" estandarizado para todos los juegos de Neureka
 * Muestra las reglas del juego e información del autor
 */
export default function HowToPlayModal({
  isOpen,
  onClose,
  gameTitle,
  gameIcon,
  objective,
  rules,
  author,
  howToPlay,
}: HowToPlayModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Overlay oscuro */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{
              opacity: 1,
              scale: [0.3, 1.05, 1],
              transition: { duration: 0.5 }
            }}
            exit={{ opacity: 0, scale: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none p-4"
          >
            <div
              className="how-to-play-modal bg-white rounded-[20px] shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto pointer-events-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Header con botón cerrar */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-[20px]">
                <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
                  <span className="text-4xl">{gameIcon}</span>
                  <span>{gameTitle}</span>
                </h2>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                  aria-label="Cerrar"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Contenido del modal */}
              <div className="px-8 py-6 text-left">
                {/* Objetivo (opcional) */}
                {objective && (
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <span className="text-2xl">🎯</span>
                      <span>Objetivo</span>
                    </h3>
                    <p className="text-gray-700 leading-relaxed">{objective}</p>
                  </div>
                )}

                {/* Reglas */}
                <div className="mb-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <span className="text-2xl">📍</span>
                    <span>Reglas</span>
                  </h3>
                  <ul className="space-y-2">
                    {rules.map((rule, index) => (
                      <li key={index} className="text-gray-700 leading-relaxed flex gap-2">
                        <span className="text-purple-600 font-bold">•</span>
                        <span className="flex-1">{rule}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Cómo se juega (opcional) */}
                {howToPlay && (
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-3 flex items-center gap-2">
                      <span className="text-2xl">🕹️</span>
                      <span>Cómo se juega</span>
                    </h3>
                    <p className="text-gray-700 leading-relaxed">{howToPlay}</p>
                  </div>
                )}

                {/* Autor */}
                {author && (
                  <div className="border-t border-gray-200 pt-4 mt-6">
                    <p className="text-sm text-gray-500 italic">
                      <strong>Autor:</strong> {author}
                    </p>
                  </div>
                )}
              </div>

              {/* Footer con botón */}
              <div className="sticky bottom-0 bg-gray-50 px-8 py-4 flex justify-center border-t border-gray-200 rounded-b-[20px]">
                <button
                  onClick={onClose}
                  className="text-white font-semibold text-base px-10 py-3 rounded-[10px] border-none hover:opacity-90 transition-opacity"
                  style={{ background: '#667eea' }}
                >
                  Entendido
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
